<script setup lang="ts">
import {ref} from "vue";
import {storeToRefs} from "pinia";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {Button} from "@/core/components/ui/button";
import Icon from "@/core/components/icon/Icon.vue";
import {FLOWCHART_INSTANCE_OPTIONS, FlowchartInstance} from "@/core/interfaces/flowchart/flowchart";
import {CloudUploadIcon, Loader2, Mail, Printer, Trash2} from "@lucide/vue";
import {flowchartApi, flowchartInstanceApi} from "@/container";
import {usePatientStore} from "@/stores/patient.store";
import {useToastStore} from "@/stores/toast.store";
import {openDMP} from "@/core/helpers/dmp";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import formatHelper from "@/core/helpers/formatHelper";
import MSSanteDialog from "@/mssante/components/MSSanteDialog.vue";
import {MSSanteAttachment, MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import useFlowchartMssante, {
    FlowchartMssanteSource,
    instanceMssanteSource
} from "@/flowcharts/composables/useFlowchartMssante";

const props = defineProps<{
    instance: FlowchartInstance;
}>();

const emit = defineEmits<{ (event: 'deleted'): void }>();

const {customer} = storeToRefs(usePatientStore());
const {showSuccess, showError} = useToastStore();

/**
 * Whether the deletion confirmation is up. Held here rather than opened by a trigger
 * of its own: the menu closes on the click and would take a nested trigger down with it.
 */
const confirming = ref(false);
const deleting = ref(false);

/**
 * Print a document again, with the products and comment saved on the terminal node.
 * @param document
 */
const print = async (document: { class: string, label: string }) => {
    const terminalOptions = props.instance.path.at(-1)?.options;

    const response = await flowchartApi().print(
        props.instance.flowchart.id,
        customer.value!,
        {
            document: document.class,
            recommendations: terminalOptions?.[FLOWCHART_INSTANCE_OPTIONS.recommendations] as number[] | undefined,
            comment: terminalOptions?.[FLOWCHART_INSTANCE_OPTIONS.comment] as string | undefined,
        },
        props.instance.path
    );

    const url = window.URL.createObjectURL(new Blob([response], {type: response.type}));
    window.open(url);
};

const menuOpen = ref<boolean>(false);

/**
 * Held here rather than nested in the menu, which closes on the click and
 * would take the dialog down with it.
 */
const dialogOpen = ref<boolean>(false);
const dialogPrefills = ref<MSSantePrefill[]>([]);
const preparing = ref<boolean>(false);

const source = ref<FlowchartMssanteSource>();
const mssante = useFlowchartMssante(() => source.value);

/**
 * Open the MSSanté dialog on the message the pharmacist was offered at the end
 * of the flowchart. The saved instance only keeps the path: the flowchart is
 * loaded again to rebuild it. Without a message to offer, the dialog opens
 * blank. The menu stays open on its loader meanwhile.
 */
const composeMSSante = async () => {
    preparing.value = true;

    try {
        const flowchart = await flowchartApi().show(props.instance.flowchart.id);

        source.value = flowchart?.nodes && customer.value
            ? instanceMssanteSource(flowchart, props.instance, customer.value)
            : undefined;

        const prefill = mssante.prefill.value;
        const attachments: MSSanteAttachment[] = [];

        if (prefill) {
            try {
                attachments.push(...await mssante.loadAttachments());
            } catch {
                showError({content: 'Les pièces jointes n\'ont pas pu être préparées.'});
            }
        }

        dialogPrefills.value = prefill ? [{...prefill, attachments}] : [];
        menuOpen.value = false;
        dialogOpen.value = true;
    } finally {
        preparing.value = false;
    }
};

/**
 * Delete the instance from the patient history. The confirmation stays up while the
 * call runs, and after a failure worth retrying. A 401/403 closes it: retrying would
 * fail the same way, and the response interceptor already toasted it.
 */
const remove = async () => {
    deleting.value = true;

    try {
        await flowchartInstanceApi().destroy(props.instance.id);
        confirming.value = false;
        showSuccess({content: 'Le résultat du TROD a été supprimé.'});
        emit('deleted');
    } catch (error) {
        if (isAuthorizationError(error)) {
            confirming.value = false;
        } else {
            showError({content: apiErrorMessage(error, 'Le résultat du TROD n\'a pas pu être supprimé.')});
        }
    } finally {
        deleting.value = false;
    }
};
</script>

<template>
  <DropdownMenu v-model:open="menuOpen">
    <DropdownMenuTrigger as-child>
      <Icon name="mdi-dots-vertical" :size="30" color="gray" />
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-56" side="top">
      <DropdownMenuGroup>
        <DropdownMenuItem @click="openDMP">
          <CloudUploadIcon />
          Accès <img src="@/assets/images/dmp.jpg" class="h-8" alt="DMP">
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="preparing" @select.prevent="composeMSSante">
          <Loader2 v-if="preparing" class="animate-spin" />
          <Mail v-else />
          Envoyer par <img class="w-16" src="@/assets/images/logos/logo-ms-sante.png" alt="MSSanté">
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem
          v-for="document in instance.verdict.documents"
          :key="`document-${document.class}`"
          @click="() => print( document)"
        >
          <Printer />
          {{ document.label }}
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        class="cursor-pointer text-red-700 focus:text-red-700"
        @click="confirming = true"
      >
        <Trash2 />
        Supprimer
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
  <MSSanteDialog
    v-model:open="dialogOpen"
    :prefills="dialogPrefills"
    :consumer-key="customer?.consumer_key"
    :patient-name="customer?.information?.name"
    :source="{type: 'flowchart-instance', id: instance.id}"
  />

  <AlertDialog v-model:open="confirming">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Supprimer ce résultat de TROD ?</AlertDialogTitle>
        <AlertDialogDescription>
          Le résultat « {{ instance.flowchart.name }} » du {{ formatHelper.readableDate(instance.created_at) }}
          sera retiré de la fiche patient. Cette action est définitive.
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter class="items-center">
        <AlertDialogCancel :disabled="deleting">
          Annuler
        </AlertDialogCancel>

        <Button
          variant="destructive"
          class="bg-red-600 hover:bg-red-600/90"
          :disabled="deleting"
          @click="remove"
        >
          Supprimer
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
